import React from 'react'
import styled from 'styled-components/macro'
import { PageTitle } from 'src/components/PageLayout/PageTitle'
import { useHasAppCopilot } from 'src/context/Gatekeeper'
import { AppCopilot } from 'src/components/Copilot/AppCopilot/AppCopilot'
import {
  AppCopilotProvider,
  useOptionalAppCopilotContext,
} from 'src/components/Copilot/AppCopilot/AppCopilot.context'
import { AppCopilotOptions } from 'src/components/Copilot/AppCopilot/AppCopilot.types'
import { AskTrusteroIntelligenceButton } from 'src/components/Copilot/AskTrusteroIntelligenceButton'
import { useIndexPageTitleIcon } from 'src/navbar/indexPageTitleIcons'
import {
  IndexPageContainer,
  IndexPageContent,
  IndexPageHeader,
  IndexPageContentTabsContainer,
  IndexPageTabsContainer,
} from '../../PageLayout/PageLayout.styles'
import { GridContainer } from '../Grid'

const IndexPageShell = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;

  > *:first-child {
    flex: 1 1 0%;
    min-width: 0;
  }
`

const IndexPageCopilotButton = ({
  shouldRenderCopilot,
}: {
  shouldRenderCopilot: boolean
}): JSX.Element | null => {
  const appCopilotContext = useOptionalAppCopilotContext()

  if (!shouldRenderCopilot || !appCopilotContext) {
    return null
  }

  return (
    <AskTrusteroIntelligenceButton
      onClick={() => appCopilotContext.setShow(!appCopilotContext.show)}
    />
  )
}

type IndexPageProps = {
  title?: string
  headerTabs?: JSX.Element
  filterBar?: JSX.Element
  actions?: JSX.Element
  message?: JSX.Element
  indexGrid?: JSX.Element
  tabs?: JSX.Element
  pagination?: JSX.Element
  modals?: JSX.Element[]
  noTitleBorder?: boolean
  copilotOptions?: AppCopilotOptions
}

export const IndexPage = ({
  title,
  actions,
  filterBar,
  message,
  indexGrid,
  tabs,
  pagination,
  modals,
  noTitleBorder,
  copilotOptions,
}: IndexPageProps): JSX.Element => {
  const hasAppCopilot = useHasAppCopilot()
  const shouldRenderCopilot = !!copilotOptions?.enabled && hasAppCopilot
  const titleIcon = useIndexPageTitleIcon(title)

  const page = (
    <IndexPageShell>
      <IndexPageContainer>
        <IndexPageHeader>
          {title && (
            <PageTitle
              title={title}
              icon={titleIcon}
              noBorder={noTitleBorder}
              rightContent={
                <IndexPageCopilotButton
                  shouldRenderCopilot={shouldRenderCopilot}
                />
              }
            />
          )}
          {actions}
          {message}
          {filterBar}
        </IndexPageHeader>
        <IndexPageContent>
          {tabs && (
            <IndexPageContentTabsContainer>
              {tabs}
            </IndexPageContentTabsContainer>
          )}
          {indexGrid && <GridContainer>{indexGrid}</GridContainer>}
          {pagination}
        </IndexPageContent>
        <>{modals?.map((modal) => modal)}</>
      </IndexPageContainer>
      {shouldRenderCopilot ? <AppCopilot /> : null}
    </IndexPageShell>
  )

  if (!shouldRenderCopilot) {
    return page
  }

  return (
    <AppCopilotProvider
      referringContext={copilotOptions.referringContext}
      referringContextMetadata={copilotOptions.referringContextMetadata}
      currentContextLabel={copilotOptions.currentContextLabel}
      initialTitle="Trustero Intelligence"
      initialShowBotTrigger={copilotOptions.showBotTrigger ?? false}
    >
      {page}
    </AppCopilotProvider>
  )
}

export const IndexPageWithTabs = ({
  title,
  tabs,
  modals,
  copilotOptions,
}: {
  title: string
  tabs: JSX.Element
  modals?: JSX.Element[]
  copilotOptions?: AppCopilotOptions
}): JSX.Element => {
  const hasAppCopilot = useHasAppCopilot()
  const shouldRenderCopilot = !!copilotOptions?.enabled && hasAppCopilot
  const titleIcon = useIndexPageTitleIcon(title)

  const page = (
    <IndexPageShell>
      <IndexPageTabsContainer>
        <IndexPageHeader>
          {title && (
            <PageTitle
              title={title}
              icon={titleIcon}
              rightContent={
                <IndexPageCopilotButton
                  shouldRenderCopilot={shouldRenderCopilot}
                />
              }
            />
          )}
        </IndexPageHeader>
        {tabs}
        {modals?.map((modal) => modal)}
      </IndexPageTabsContainer>
      {shouldRenderCopilot ? <AppCopilot /> : null}
    </IndexPageShell>
  )
  if (!shouldRenderCopilot) {
    return page
  }

  return (
    <AppCopilotProvider
      referringContext={copilotOptions.referringContext}
      referringContextMetadata={copilotOptions.referringContextMetadata}
      currentContextLabel={copilotOptions.currentContextLabel}
      initialTitle="Trustero Intelligence"
      initialShowBotTrigger={copilotOptions.showBotTrigger ?? false}
    >
      {page}
    </AppCopilotProvider>
  )
}
